import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import ProfileHeader from './components/ProfileHeader';
import ProfileHighlights from './components/ProfileHighlights';
import AlertsCard from './components/AlertsCard';
import AICallSummaryCard from './components/AICallSummaryCard';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { useCandidate } from '../../hooks';
import { useAuth } from '../../contexts/AuthContext';
import {
  getCandidateAlerts,
  getCandidateCalls,
  deleteCandidate,
  getPrioritizedCandidatesCount,
  uploadCandidatesToZohoList,
} from '../../lib/api';
import { buildCandidateProfileView } from '../../lib/utils/candidateProfileView';
import { CANDIDATES_LIST_PATH } from '../../lib/utils/candidateListRoutes';
import ZohoUploadResult from '../../components/ZohoUploadResult';

const MAX_PRIORITIZED = 5;

const CandidateProfile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { candidateId } = useParams();
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [calls, setCalls] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadNotice, setUploadNotice] = useState(null);
  
  const { candidate: dbCandidate, loading, error, updateCandidate } = useCandidate(candidateId);
  
  // Fetch candidate alerts and last call
  useEffect(() => {
    const fetchAlerts = async () => {
      if (!candidateId) return;
      
      try {
        setLoadingAlerts(true);
        const alertsData = await getCandidateAlerts(candidateId);
        let callsData = [];

        try {
          callsData = await getCandidateCalls(candidateId);
        } catch (callErr) {
          console.error('Error fetching candidate calls:', callErr);
        }

        setAlerts(alertsData || []);
        setCalls(callsData);
      } catch (err) {
        console.error('Error fetching candidate alerts:', err);
      } finally {
        setLoadingAlerts(false);
      }
    };
    
    fetchAlerts();
  }, [candidateId]);

  const candidate = useMemo(
    () => buildCandidateProfileView(dbCandidate, alerts, calls),
    [dbCandidate, alerts, calls]
  );

  const handleSyncToATS = async () => {
    if (!user?.id || syncing) return;

    const zohoId = String(dbCandidate?.zoho_id ?? '').trim();
    if (!zohoId) return;

    setUploadNotice(null);
    setUploadResult(null);
    setSyncing(true);
    try {
      const result = await uploadCandidatesToZohoList(user.id, [zohoId]);
      setUploadResult(result);
    } catch (err) {
      console.error('Error syncing to ATS:', err);
      setUploadNotice({
        type: 'error',
        message: err?.message || t('candidateProfile.syncFailed'),
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleRejectSync = async () => {
    if (!candidateId) return;
    
    console.log('Rejecting sync for:', candidate?.name);
    
    try {
      // Update blacklist flag
      await updateCandidate({ blacklist: true });
      alert(t('candidateProfile.blacklistSuccess', { name: candidate?.name }));
      navigate(listReturnPath);
    } catch (err) {
      console.error('Error updating candidate:', err);
      alert(t('candidateProfile.updateFailed'));
    }
  };

  const listReturnPath = location.state?.returnTo || CANDIDATES_LIST_PATH;

  const handleBack = () => {
    navigate(listReturnPath);
  };

  const handleUpdateContact = async ({ email, phone }) => {
    if (!candidateId) return;

    await updateCandidate({
      email: email || null,
      phone: phone || null,
    });
  };

  const handleTogglePriority = async () => {
    if (!candidateId || !candidate) return;

    const newPriorityValue = !candidate.isPrioritized;

    if (newPriorityValue && user?.id) {
      try {
        const prioritizedCount = await getPrioritizedCandidatesCount(user.id);
        if (prioritizedCount >= MAX_PRIORITIZED) {
          setShowLimitDialog(true);
          return;
        }
      } catch (err) {
        console.warn('getPrioritizedCandidatesCount failed:', err);
      }
    }

    try {
      await updateCandidate({ prioritize: newPriorityValue }, { updateTimestamp: false });
    } catch (err) {
      console.error('Failed to update priority:', err);
    }
  };

  const handleSetOpenToWorkManual = async (isOpen) => {
    if (!candidateId) return;
    await updateCandidate({
      is_open_to_work: isOpen ? 'true' : 'false',
      is_open_to_work_manual: true,
      is_open_to_work_manual_at: new Date().toISOString(),
    });
  };

  const handleReturnAiWatch = async () => {
    if (!candidateId) return;
    await updateCandidate({
      is_open_to_work_manual: false,
      is_open_to_work_manual_at: null,
    });
  };

  const handleDeleteCandidate = async () => {
    if (!candidateId) return;
    try {
      await deleteCandidate(candidateId);
      navigate(listReturnPath);
    } catch (err) {
      console.error('Error deleting candidate:', err);
      alert(t('candidateProfile.deleteFailed'));
    }
  };

  // Redirect if no candidate ID
  if (!candidateId) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('candidateProfile.noCandidateSelected')}</h2>
            <button
              onClick={handleBack}
              className="text-primary hover:text-accent underline"
            >
              {t('candidateProfile.backToCandidatesList')}
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Loading state
  if (loading || loadingAlerts) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('candidateProfile.loading')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Error state
  if (error || !candidate) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="text-red-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">{t('candidateProfile.failedToLoad')}</h2>
            <p className="text-muted-foreground mb-4">{error || t('candidateProfile.candidateNotFound')}</p>
            <button
              onClick={handleBack}
              className="text-primary hover:text-accent underline"
            >
              {t('candidateProfile.backToCandidatesList')}
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('candidateProfile.pageTitle', { name: candidate?.name })}</title>
        <meta
          name="description"
          content={t('candidateProfile.pageDescription', { name: candidate?.name })} />

      </Helmet>
      <MainLayout>
        <div className="min-h-screen bg-background">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            <Button
              variant="outline"
              size="sm"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={handleBack}
              className="mb-4 bg-white hover:bg-accent hover:text-accent-foreground"
            >
              {t('candidateProfile.backToList')}
            </Button>

            {/* Page Title */}
            <div className="mb-6 md:mb-8">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                {t('candidateProfile.title')}
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                {t('candidateProfile.subtitle')}
              </p>
            </div>

            {/* Profile Content */}
            <div className="space-y-4 md:space-y-6 lg:space-y-8">
              {/* Header Section */}
              <ProfileHeader
                candidate={candidate}
                onDeleteCandidate={handleDeleteCandidate}
                onUpdateContact={handleUpdateContact}
                onSyncToATS={handleSyncToATS}
                syncing={syncing}
                onTogglePriority={handleTogglePriority}
                onSetOpenToWorkManual={handleSetOpenToWorkManual}
                onReturnAiWatch={handleReturnAiWatch}
                isPrioritized={candidate.isPrioritized}
                hasPhone={Boolean(candidate.phone?.trim())}
              />

              {uploadNotice && (
                <div className={`rounded-lg border px-4 py-3 text-sm ${
                  uploadNotice.type === 'success'
                    ? 'border-success/30 bg-success/10 text-success'
                    : 'border-destructive/30 bg-destructive/10 text-destructive'
                }`}>
                  {uploadNotice.message}
                </div>
              )}

              {uploadResult && !syncing && (
                <ZohoUploadResult result={uploadResult} />
              )}

              <ProfileHighlights candidate={candidate} />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
                <AlertsCard alerts={alerts} />
                <AICallSummaryCard candidate={candidate} />
              </div>

              <Button
                variant="outline"
                size="sm"
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={handleBack}
                className="bg-white hover:bg-accent hover:text-accent-foreground"
              >
                {t('candidateProfile.backToList')}
              </Button>
            </div>
          </div>
        </div>
      </MainLayout>
      {createPortal(
        <ConfirmDialog
          isOpen={showLimitDialog}
          onClose={() => setShowLimitDialog(false)}
          onConfirm={() => setShowLimitDialog(false)}
          title={t('candidates.prioritizationLimitTitle')}
          message={t('candidates.prioritizationLimitMessage', { count: MAX_PRIORITIZED })}
          confirmText={t('common.ok')}
          variant="default"
          hideCancel
        />,
        document.body
      )}
    </>);

};

export default CandidateProfile;
